import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type SearchHandler = (query: string) => void | Promise<void>;

interface WorldMapContextValue {
  isSearchReady: boolean;
  runSearch: (query: string) => Promise<boolean>;
  registerSearchHandler: (handler: SearchHandler | null) => void;
}

const WorldMapContext = createContext<WorldMapContextValue | null>(null);

export function WorldMapProvider({ children }: { children: ReactNode }) {
  const handlerRef = useRef<SearchHandler | null>(null);
  const [isSearchReady, setSearchReady] = useState(false);

  const registerSearchHandler = useCallback((handler: SearchHandler | null) => {
    handlerRef.current = handler;
    setSearchReady(handler != null);
  }, []);

  const runSearch = useCallback(async (query: string) => {
    const fn = handlerRef.current;
    if (!fn) return false;
    await fn(query);
    return true;
  }, []);

  const value = useMemo<WorldMapContextValue>(
    () => ({
      isSearchReady,
      runSearch,
      registerSearchHandler,
    }),
    [isSearchReady, runSearch, registerSearchHandler],
  );

  return (
    <WorldMapContext.Provider value={value}>{children}</WorldMapContext.Provider>
  );
}

export function useWorldMap(): WorldMapContextValue {
  const ctx = useContext(WorldMapContext);
  if (!ctx) {
    throw new Error("useWorldMap must be used inside <WorldMapProvider>");
  }
  return ctx;
}

export function useWorldMapOptional(): WorldMapContextValue | null {
  return useContext(WorldMapContext);
}
